import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button, Skeleton } from '@argink/ui';
import { ErrorBlock } from '../components/layout/StateBlock';
import { useAuth } from './AuthContext';

/**
 * Guarda de rota. Sem sessão válida, volta para `/login` preservando o destino
 * pretendido (`state.from`) para redirecionar de volta depois do login.
 *
 * A guarda decide sobre o `status` do probe, e não sobre um booleano: "não estou
 * autenticado" e "não consegui saber" pedem telas diferentes. Mandar para
 * `/login` quando o backend está fora do ar diria ao usuário que a sessão dele
 * acabou — mentira, e uma que esconde o incidente real (§34). Ele tentaria
 * logar, o OTP falharia, e o suporte receberia "não consigo entrar" no lugar de
 * "o serviço está fora".
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, sessionFailure, retrySession } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="page-loading" aria-hidden={false}>
        <Skeleton lines={3} label="Verificando sessão" />
      </div>
    );
  }

  if (status === 'infrastructure-failure') {
    return (
      <div className="page-loading">
        <ErrorBlock
          title="Não foi possível verificar sua sessão"
          description={
            <>
              <p>
                O serviço não respondeu. Sua sessão não foi encerrada. Tente novamente em alguns
                instantes.
              </p>
              {/* O detalhe técnico fica visível de propósito: sem ele, um incidente
                * de infraestrutura chega ao suporte descrito como "não entra". */}
              {sessionFailure ? (
                <p className="state-block__detail">Detalhe técnico: {sessionFailure.message}</p>
              ) : null}
            </>
          }
          action={
            <Button variant="outline" size="sm" onClick={retrySession}>
              Tentar novamente
            </Button>
          }
        />
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
